import "./Contracts.scss";
import Footer from "../../components/Footer/Footer";
import Header from "../../components/Assessment/Header/Header";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AssessmentCheckboxRadio,
  TemplateSearch,
  Pagination,
  DetailedListItem,
  SearchTypeSelect,
  SkeletonList,
  RightSidePanel,
} from "ui-components";
import { setSessionStorageItem } from "../../utils/SessionStorageMethod/setSessionStorageItem";
import ContractsIcon from "../../utils/Images/ContractsIcon";
import {
  getContractList,
  getContractVendors,
} from "../../service/StaffAugumentation/StaffAugumentationApis";
import useDebounce from "../../utils/Hooks/useDebounce";
import {
  convertDate,
  copyText,
  scrollWindowTop,
} from "../../utils/Common/Constant";
import DataNotAvailable from "../../utils/DataNotFound/DataNotAvailable";
import { ResponseScreenEnums } from "../../utils/Enums/ResponseScreenEnums";
import CreateContractForm from "../CvScanning/components/CreateContractForm/CreateContractForm";
import i18n from "../../i18nConfig";

const Contracts = () => {
  const [isResetDisable] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const paginaionRef = useRef<any>(null);
  const [pageSize, setPageSize] = useState(10);
  const [managePaginationRef, setManagePaginationRef] = useState<any>(null);
  const [vendorFilter, setVendorFilter] = useState<any>("");
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentLanguage = i18n.language;
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateContractPanel, setShowCreateContractPanel] = useState(null);
  const status = searchParams.get("st") ?? "Active";
  const searchText = searchParams.get("search") || "";
  const vendorIdFromUrl = searchParams.get("vendorId") || "";
  const debounceSearchText = useDebounce(searchText, 500);
  const currentPage = searchParams.get("page")
    ? Number(searchParams.get("page"))
    : 1;
  const [contractList, setContractList] = useState<any>([]);
  const [vendorOptions, setVendorOptions] = useState<any>([]);
  const getContractListApi = async () => {
    setIsLoading(true);
    const response = await getContractList(
      searchText,
      status,
      vendorFilter?.value ?? "",
      currentPage,
      pageSize,
    );
    if (response?.status === 200) {
      setContractList(response?.data?.data);
      setTotalCount(response?.data?.count);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  };
  const getContractVendorsApi = async () => {
    const response = await getContractVendors(vendorFilter.label);
    if (response?.status === 200) {
      const vendorOptions = response?.data?.data?.map((vendor: any) => ({
        label: vendor?.partnerUserName,
        value: vendor?.partnerUserId,
        partnerCompanyId: vendor?.partnerCompanyId,
      }));
      setVendorOptions(vendorOptions);

      // Auto-select vendor if vendorId is provided in URL
      if (vendorIdFromUrl) {
        const selectedVendor = vendorOptions.find(
          (vendor: any) => vendor.partnerCompanyId === vendorIdFromUrl,
        );
        if (selectedVendor) {
          setVendorFilter(selectedVendor);
        }
      }
    }
  };
  useEffect(() => {
    getContractVendorsApi();
    scrollWindowTop();
  }, [vendorIdFromUrl, vendorFilter.label]);
  useEffect(() => {
    if (paginaionRef.current && managePaginationRef != null) {
      paginaionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [managePaginationRef]);
  useEffect(() => {
    getContractListApi();
  }, [
    status,
    currentPage,
    debounceSearchText,
    vendorFilter,
    showCreateContractPanel,
  ]);
  const handleFilter = ({ type, value }: any) => {
    let params: Record<string, any> = {
      st: status,
      page: currentPage,
      search: searchText,
      vendorId: vendorFilter?.partnerCompanyId ?? vendorIdFromUrl ?? "",
    };

    switch (type) {
      case "status":
        params = {
          ...params,
          st: value,
          page: !isResetDisable ? currentPage : 1,
        };
        break;
      case "page":
        params = { ...params, page: value };
        break;
      case "search":
        params = { ...params, search: value, page: 1 };
        break;
      case "vendorId":
        params = { ...params, vendorId: value, page: 1 };
        break;
    }

    const queryString = new URLSearchParams(params).toString();
    navigate(`?${queryString}`);
  };

  return (
    <>
      <Header />
      <div className="minHeight100 contractPageContainer">
        <div className="container-fluid">
          <div className="contractLeftSection">
            <h2 className="mb-4">{t("RESUME.CONTRACTS")}</h2>
            <div className="contractFilterSection">
              <div className="filterContract">
                <div className="checkbox">
                  <div
                    className="checkboxWithLabel"
                    onClick={() => {
                      handleFilter({
                        type: "status",
                        value: ResponseScreenEnums.STATUS_ACTIVE,
                      });
                    }}
                  >
                    <AssessmentCheckboxRadio
                      type="radio"
                      checked={status === ResponseScreenEnums.STATUS_ACTIVE}
                    />
                    <p className="radioCheckLabel">{t("GENERAL.PUBLISHED")}</p>
                  </div>
                  <div
                    className="checkboxWithLabel"
                    onClick={() => {
                      handleFilter({
                        type: "status",
                        value: ResponseScreenEnums.STATUS_DRAFT,
                      });
                    }}
                  >
                    <AssessmentCheckboxRadio
                      type="radio"
                      checked={status === ResponseScreenEnums.STATUS_DRAFT}
                    />
                    <p className="radioCheckLabel">{t("VENDOR.PENDING")}</p>
                  </div>
                </div>
              </div>

              <div className="searchWithNew">
                <TemplateSearch
                  onChange={(e) => {
                    handleFilter({ type: "search", value: e });
                  }}
                  iconName="searchList"
                  placeholder={t("CONTRACT.SEARCH")}
                  value={searchText ?? ""}
                />
              </div>
              <div className="vendorSearch">
                <SearchTypeSelect
                  label={t("CONTRACT.VENDOR")}
                  options={vendorOptions}
                  value={vendorFilter}
                  placeholder={t("VENDOR.SELECT_VENDOR")}
                  onChange={(value: any) => {
                    setVendorFilter(value);
                    handleFilter({
                      type: "vendorId",
                      value: value?.partnerCompanyId ?? "",
                    });
                  }}
                />
              </div>
            </div>
          </div>
          <div className="contractCards" ref={paginaionRef}>
            <p className="totalContracts">
              {t("CONTRACT.TOTAL_CONTRACTS")} : {totalCount}
            </p>
            {isLoading ? (
              <>
                {Array.from({ length: 5 }).map((_, index) => (
                  <SkeletonList
                    key={`skeleton-${index}`}
                    numberOfTitles={1}
                    numberofTextLines={5}
                    titleSize={18}
                    textSize={12}
                    thumbnailHeight={250}
                    thumbnailWidth={250}
                    itemsGap={20}
                  />
                ))}
              </>
            ) : contractList?.length ? (
              vendorIdFromUrl && !vendorFilter?.value ? (
                <div className="minHeight40 pt-5">
                  <DataNotAvailable
                    text={"RESOURCE.NO_MATCHING_CONTRACTS_FOUND"}
                  />
                </div>
              ) : (
                contractList.map((item: any, ind: any) => (
                  <div key={ind}>
                    <DetailedListItem
                      details={[
                        {
                          label: t("CONTRACT.VENDOR"),
                          value: item?.partnerCompanyName ?? "-",
                        },
                        {
                          label: t("CONTRACT.MANAGER"),
                          value: item?.partnerUserName ?? "-",
                        },
                        {
                          label: t("CONTRACT.RESOURCE_NAME"),
                          value: `${item?.applicantFirstName || item?.applicantLastName ? item?.applicantFirstName + " " + item?.applicantLastName : "-"}`,
                        },
                        {
                          label: t("CONTRACT.SHIFT_TYPE"),
                          value: `${item?.meta?.shiftStartTime} - ${item?.meta?.shiftEndTime} (${item?.meta?.timezone})`,
                        },
                        {
                          label: t("CONTRACT.VENDOR_EMAIL"),
                          value: item?.["partnerCompany.email"] ?? "-",
                          onLabelClick: () =>
                            copyText(item?.["partnerCompany.email"]),
                          valueClassName: "cursorPointer",
                        },
                        {
                          label: t("CONTRACT.RESOURCE_EMAIL"),
                          value: item?.applicantEmailId ?? "-",
                          onLabelClick: () => copyText(item?.applicantEmailId),
                          valueClassName: "cursorPointer",
                        },
                      ]}
                      titleBelow={
                        <div className="titleBelowContract">
                          {item?.meta?.startDate && (
                            <div className="belowTexts">
                              <span className="key">
                                {t("CONTRACT.START_DATE")} :
                              </span>
                              <span className="value">
                                {item?.meta?.startDate
                                  ? convertDate(
                                      item?.meta?.startDate,
                                      true,
                                      false,
                                    )
                                  : "-"}
                              </span>
                            </div>
                          )}
                          {item?.contractType && (
                            <div className="belowTexts">
                              <span className="key">
                                {t("CONTRACT.CONTRACT_TYPE")} :
                              </span>
                              <span className="value">
                                {item?.contractType ? item?.contractType : "-"}
                              </span>
                            </div>
                          )}
                          {item?.jobLocation && (
                            <div className="belowTexts">
                              <span className="key">
                                {t("CREATE_JOB_LOCATION")} :
                              </span>
                              <span className="value">{item?.jobLocation}</span>
                            </div>
                          )}
                        </div>
                      }
                      statusText={item?.status}
                      statusType={""}
                      title={item?.title ?? "-"}
                      statusIcon={item?.companyLogo ?? <ContractsIcon />}
                      actionButtonText={
                        item?.status == ResponseScreenEnums.STATUS_DRAFT
                          ? t("GENERAL.EDIT")
                          : ""
                      }
                      onActionClick={() => {
                        setShowCreateContractPanel(item?.jobContractsId);
                      }}
                      secondaryButtonText={t("GENERAL.VIEW")}
                      onSecondaryClick={() => {
                        navigate(
                          `/${currentLanguage}/contractDetails/${item?.jobContractsId}`,
                        );
                      }}
                    />
                    {showCreateContractPanel === item?.jobContractsId && (
                      <RightSidePanel
                        show={true}
                        handleClose={() => setShowCreateContractPanel(null)}
                        title={
                          item?.applicantFirstName && item?.applicantLastName
                            ? `${item?.applicantFirstName} ${item?.applicantLastName}`
                            : `${item?.applicantEmailId?.toLowerCase()}`
                        }
                        className="rightSidePanelSection jobTheme createContractPanel createContractModal" // Added a specific class for styling if needed
                      >
                        <CreateContractForm
                          onClose={() => setShowCreateContractPanel(null)}
                          jobcontractId={item?.jobContractsId}
                        />
                      </RightSidePanel>
                    )}
                  </div>
                ))
              )
            ) : (
              <div className="p-5">
                {contractList.length || searchText.length ? (
                  <DataNotAvailable text={"GENERAL.DATA_NOT_FOUND"} />
                ) : (
                  <DataNotAvailable text={"GENERAL.DATA_NOT_FOUND"} />
                )}
              </div>
            )}
          </div>
          <div className="row">
            <div className="col-md-12">
              <Pagination
                className="pagination-bar"
                currentPage={Number(currentPage)}
                totalCount={totalCount > pageSize ? totalCount : 0}
                pageSize={pageSize}
                onPageChange={(page: number) => {
                  handleFilter({ type: "page", value: page });
                  setSessionStorageItem("contractListPage", page);
                  setManagePaginationRef(Math.random());
                }}
                ofLabel={t("GENERAL.OF")}
                resultLabel={t("GENERAL.RESULTS")}
                setPageSize={setPageSize}
              />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Contracts;
